import React, { useState, useEffect } from 'react';
import { Phone, Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { businessInfo } from '../mockData';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenus = () => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  };

  const scrollToSection = (sectionId) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  // Main nav items (links to pages)
  const mainNavItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/our-services' },
    { label: 'Special Offers', path: '/exclusive-deals' },
    { label: 'Gallery', path: '/our-work' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/book-inspection' }
  ];

  // Dropdown items for Google Ads campaigns
  const dropdownItems = [
    { label: 'Book Free Inspection', path: '/book-inspection' },
    { label: 'Free Roof Assessment', path: '/free-assessment' },
    { label: 'Our Services', path: '/our-services' },
    { label: 'Exclusive Deals', path: '/exclusive-deals' },
    { label: 'Why Metal Roofing', path: '/why-metal-roofing' },
    { label: 'Our Work Gallery', path: '/our-work' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => scrollToSection('home')} className="flex items-center space-x-3">
            <div className="bg-white p-1 rounded">
              <img 
                src="https://customer-assets.emergentagent.com/job_roof-booking-hub/artifacts/qm10d648_IMG_0732.jpeg"
                alt="Dezire Metal Roofing"
                className="h-12 w-auto"
                style={{ 
                  mixBlendMode: 'darken',
                  filter: 'brightness(1.5) contrast(1.2)'
                }}
              />
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {mainNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMenus}
                className={`font-medium transition-colors ${
                  location.pathname === item.path 
                    ? 'text-primary' 
                    : 'text-gray-700 hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Dropdown Menu */}
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-1 font-medium text-gray-700 hover:text-primary transition-colors"
              >
                <span>More</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                  {dropdownItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMenus}
                      className="block px-4 py-2 text-gray-700 hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="/call"
              className="flex items-center space-x-2 text-gray-700 hover:text-primary"
            >
              <Phone className="w-4 h-4" />
              <span className="font-semibold">{businessInfo.phone}</span>
            </a>
            <Link to="/book-inspection">
              <Button className="bg-primary hover:bg-accent text-white">
                Book Inspection
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden text-gray-900"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t pt-4">
            <nav className="flex flex-col space-y-2">
              {mainNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMenus}
                  className={`text-left font-medium py-2 ${
                    location.pathname === item.path 
                      ? 'text-primary' 
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              
              <div className="border-t pt-2 mt-2">
                <p className="text-sm text-gray-500 mb-2">Campaign Pages:</p>
                {dropdownItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={closeMenus}
                    className="block text-left font-medium py-2 text-gray-600 hover:text-primary"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
              
              <div className="border-t pt-2 mt-2">
                <a
                  href="/call"
                  className="flex items-center space-x-2 text-gray-700 hover:text-primary py-2"
                >
                  <Phone className="w-4 h-4" />
                  <span className="font-semibold">{businessInfo.phone}</span>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
